// Sentry must be initialized BEFORE any other module so its global error
// handlers are installed before downstream code can throw. DSN comes from
// the tenant's `sentry` integration block; no-op if not configured.
import '@/lib/sentry-init'

import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { Toaster } from 'react-hot-toast'
import { I18nProvider } from '@cravo/i18n'
import { initCurrencyConfig } from '@cravo/utils'
import App from './App'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { PageLoader } from '@/components/PageLoader'
import { PwaUpdater } from '@/components/PwaUpdater'
import { InstallAppPrompt } from '@/components/InstallAppPrompt'
// Side-effect import: registers the beforeinstallprompt listener BEFORE React mounts
// so we never miss an early-firing event.
import '@/lib/install-prompt'
import './styles/index.css'

// Synchronous currency seed before render: reads the tenant's currency from
// the middleware-inlined __TENANT_CONFIG__.branding.localization so the
// first paint of every page shows the correct symbol/locale. Async refresh
// from `/platform-config/currency` is kicked off in App.tsx alongside the
// integrations bootstrap (needs API_BASE_URL resolution).
initCurrencyConfig()

// Recover from stale chunk hashes after a new deploy.
// After we ship a new build, the route-level `lazy(() => import(...))` calls
// reference the NEW chunk filenames. A tab that loaded the previous
// `index.html` (or that the service worker is serving from precache) still
// references the OLD chunk filenames — those files no longer exist on the
// origin and the dynamic import rejects with "Failed to fetch dynamically
// imported module". When that happens, the safest recovery is to force a
// fresh page load so the browser picks up the new HTML + new chunk hashes.
// The sessionStorage flag prevents an infinite reload loop if the failure
// is for some non-deploy reason (offline, misrouted request, etc.).
const STALE_CHUNK_RELOAD_KEY = '__stale_chunk_reload__'
function isStaleChunkError(reason: unknown): boolean {
  const message = reason instanceof Error ? reason.message : String(reason ?? '')
  return (
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('Importing a module script failed') ||
    /Loading chunk \d+ failed/.test(message)
  )
}
function recoverFromStaleChunk(reason: unknown) {
  if (!isStaleChunkError(reason)) return
  try {
    const alreadyTried = sessionStorage.getItem(STALE_CHUNK_RELOAD_KEY)
    if (alreadyTried) return
    sessionStorage.setItem(STALE_CHUNK_RELOAD_KEY, '1')
  } catch { /* sessionStorage blocked — best-effort */ }
  // Bypass HTTP cache so the new index.html is fetched, which in turn
  // references the new chunk hashes.
  window.location.reload()
}
window.addEventListener('error', (event) => recoverFromStaleChunk(event.error ?? event.message))
window.addEventListener('unhandledrejection', (event) => recoverFromStaleChunk(event.reason))
// Once the app boots successfully, clear the guard so subsequent stale
// chunks (later deploys) can trigger their own recovery.
window.addEventListener('load', () => {
  try { sessionStorage.removeItem(STALE_CHUNK_RELOAD_KEY) } catch { /* ignore */ }
})

// Apply tenant branding at runtime so favicon/title stay correct even when
// the service worker serves a cached HTML shell without middleware injection.
// Also force light-mode CSS vars — vendor app does not support dark mode.
const _tc = window.__TENANT_CONFIG__
if (_tc) {
  const _logos = _tc.branding?.logos
  const _favicon = _logos?.favicon || _logos?.square || _logos?.primary
  if (_favicon) {
    document.querySelectorAll<HTMLLinkElement>('link[rel="icon"], link[rel="apple-touch-icon"]')
      .forEach(el => { el.href = _favicon! })
  }
  const _name = _tc.branding?.name || _tc.tenant?.name
  if (_name) document.title = `${_name} Partner`
  const _c = _tc.branding?.colors
  if (_c?.primary) {
    document.querySelector<HTMLMetaElement>('meta[name="theme-color"]')?.setAttribute('content', _c.primary)
  }
  // Inline styles on <html> override all stylesheets including dark-mode media queries.
  const root = document.documentElement
  if (_c?.primary)    root.style.setProperty('--brand-primary', _c.primary)
  if (_c?.text)       root.style.setProperty('--brand-text', _c.text)
  // NOTE: Intentionally NOT propagating `_c.background` to `--brand-bg` here.
  // The "Background" color set in the LabelGenic admin is meant for the
  // STOREFRONT (customer-facing) page background — but the vendor portal
  // applies `--brand-bg` to `body { background }`. When a tenant picks a
  // saturated dark color (e.g. #6D28D9 purple) it floods the entire vendor
  // dashboard, drowning the white cards. Customer webapp deliberately
  // doesn't propagate this either; mirror that behavior here so the vendor
  // shell stays light and only `--brand-primary` (red/orange/blue/whatever)
  // tints the accent surfaces.
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ErrorBoundary>
      <I18nProvider fallback={<PageLoader />}>
        <BrowserRouter>
          <App />
          <PwaUpdater />
          <InstallAppPrompt />
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 3000,
              style: {
                borderRadius: '8px',
                background: '#1e293b',
                color: '#fff',
                fontSize: '14px',
              },
            }}
          />
        </BrowserRouter>
      </I18nProvider>
    </ErrorBoundary>
  </React.StrictMode>,
)
